import { TrainingLevel, TrainingLevels } from './training-level';
import { BodyCompositionType, SetType } from '../enums';
import { SetStats } from './set-stats';
import { PouchDoc } from './pouch';
import { FitnessState } from './user-state';
import { ExerciseConstants, getExerciseConstantsTable, MuscleFactors, MuscleId } from '../workout-solver-interface';
import { AdjustmentActionReason } from '../plan-update-internals';
import { deepClone, deepCompareEQ } from '../deep-helpers';
import { dropAdjustmentsThatEmptyTheExercise, getEffectivePlanSetCount } from './plan-set-count';
export class Plan_ExercisePath {
  intensityLevel: IntensityLevel;
  // Type of first set to do on the next workout.
  nextWorkoutSetType: SetType;
  nextWorkoutNonRMTestRequired?: boolean;
  // measured in rep testing
  target_reps?: number;
  /**
   * @deprecated Superseded by ExerciseStat.preferRepTargetByIntensity (Phase 2A, landed cb99c4c94,
   * 2026-04-29). Kept on the type so the lazy on-read migration
   * `migrateOriginalTargetRepsToExerciseStat` in `client/src/services/user-db.migrations.ts` can still lift values for
   * users who haven't opened the app since Phase 2A landed — removing it would silently strand
   * their stated preferences. Display sites read this as the middle term of a three-deep fallback
   * (`prefer ?? originalTargetReps ?? target_reps`). Final removal (was "Phase 2B") deferred
   * indefinitely with no concrete trigger; the migration is cheap and idempotent. See
   * `design-docs/exercise-replacement.md`. Historical PRDs at
   * `git show 38fe7d484:plans/rep-target-data-model-phase1.md` and `:phase2.md`.
   */
  originalTargetReps?: number;
  // minimum reps for linear progression - basically what was done in the last set +1
  min_reps?: number;
  /**
   * calculated from measuredRM and intensity (initially) then updated
   *
   * NOTE: This is _added_ weight, not _net_ weight! Spec section "Bodyweight exercises" defines special cases where we
   * actually consider net weight during progression loops. But in general, the target for progression is added weight.
   * */
  target_weight_kg?: number;
  /** original target, used to rewind in case of rep target adjustments in DUP and TUP  */
  orgTarget_weight_kg?: number;
  reachedMaxWeightConfiguration?: number;

  /** Stats of the last relevant set (the first set in the last WO of this path).
   * This is used to check RM progression if other metrics don't register any progression. */
  lastStats?: SetStats;
  /** for plateau detection - value that was reached when deload was last needed */
  lastDeload1rm_net_kg?: number;
  volumeAdjustment?: -1 | 1 | 0;
  /** workout log ID where autoregulation was detected, similar to plateauWorkoutLog_id */
  autoregulationWorkoutLog_id?: string;
  /** type of autoregulation detected: 'increase' or 'decrease' */
  autoregulationType?: 'increase' | 'decrease';
  /** index of the set that triggered the autoregulation */
  autoregulationTriggerSetIndex?: number;
  /**
   * The `volumeAdjustment` value the pending suggestion asked for, exactly as
   * `computeVolumeAutoregulation` decided it. Written when the suggestion is
   * raised and deleted when it is answered, alongside the three fields above.
   *
   * It exists because the answer can arrive in a LATER workout than the one
   * that raised the suggestion, so the value has to travel with the suggestion.
   * Without it the accept path had to work the value out again from the
   * direction alone, by stepping `volumeAdjustment` one up or down — which
   * cannot express a two-step move and so stored a value the rule never
   * decided. See `design-docs/volume-autoregulation.md`.
   *
   * Undefined on a suggestion raised by a build from before this field existed;
   * `resolveAcceptedVolumeAdjustment` falls back to the old step for those.
   */
  autoregulationNewVolumeAdjustment?: -1 | 0 | 1;
  /**
   * The baseline set count the pending suggestion was measured against — the
   * sub-slot's own `defaultVolume_sets`, myo-rep translated, as it stood when
   * the suggestion was raised. Written and deleted alongside the four fields
   * above.
   *
   * It exists because `autoregulationNewVolumeAdjustment` is an ABSOLUTE
   * position relative to that baseline rather than a step, while the dialog
   * offers the user a step ("by 1 set"). Three writers move the baseline while
   * a suggestion is still unanswered — an explicit set-count write from the
   * exercise Settings page or from Volume Optimization (both via
   * `setPlanSetCount`), and a program rebuild, which writes a fresh base while
   * `carrySubSlotStateAcrossRebuild` carries the paths over. Once it has moved,
   * the stored position no longer means what the user was told, and accepting
   * could store the value the exercise already had: the card would go away and
   * the set count would not move. `resolveAcceptedVolumeAdjustment` compares
   * this against the baseline at answer time and steps instead when they
   * differ. See `design-docs/volume-autoregulation.md`.
   *
   * Undefined on a suggestion raised by a build from before this field existed,
   * and those answer exactly as they did before it.
   */
  autoregulationBaselineSets?: number;
  /**
   * True when a coach set this path's next weight by hand, or a coach's intensity switch built
   * this path (#6428). The prescription cap (#5176) skips such a path for one session, because
   * the coach chose the weight on purpose; the first set conclusion on the path clears it.
   * Written only by `prepareChange` in client/src/services/coach-exercise-change.ts. See
   * `design-docs/coach-exercise-editing.md`.
   */
  coachSetWeight?: true;
}
export enum IntensityLevel {
  Initial = 100,
  Intensified = 101,
  DUPlight = 210,
  DUPheavy = 290,
  TUPlight = 310,
  TUPmedium = 350,
  TUPheavy = 390,
}
/** map of maps: per training level, then per intensity level */
export interface ExerciseIntensities {
  [key: number]: {
    [key: number]: number /** key: IntensityLevel */;
  };
}

export class Plan_SubSlot {
  trainingLevel: TrainingLevel;
  // subslot is identified by slot id (direct reference to the slot in the db, in the form "slot/$<name>")...
  slotId: string;
  // ... and index of starting exercise
  startIndex: number;
  // which exercise is currently in this slot
  exercise_id: string;
  // periodization paths - 1 if no periodization, 2 for DUP, 3 for TUP
  exercisePaths: Plan_ExercisePath[];
  // path to use at next workout
  nextWorkoutIntensity?: IntensityLevel;
  /**
   * Signal that periodization paths need to change at next WO. If set to `IntensityLevel.Initial` means exercise needs to change.
   * This process will tear down and rebuild the `exercisePaths` as well, if needed!
   */
  rebuildPathsForIntensity?: IntensityLevel;
  // WO solver sends this from the server, so the client can do progression in offline mode
  intensities: ExerciseIntensities;
  /** Number of sets per workout for this slot as prescribed by server-side planner.
   * Actual volume for each workout may be adjusted by volume autoregulation logic. */
  defaultVolume_sets: number;
  lastTimeConcluded_utc?: string;
  plateauWorkoutLog_id?: string;
  /**
   * The exercise the APP put in this sub-slot on its own, when it did: a lift swapped
   * out after a plateau or a weakness test, or a rotation at workout load away from an
   * avoided, quarantined or deleted custom exercise. `rotateExerciseInSubSlotByApp` in
   * client/src/services/exercise-switching.ts sets it, and `replaceExerciseInSubSlot`
   * clears it on every other swap — including a hand swap that keeps this sub-slot.
   *
   * It exists for {@link hasHandEditsSinceProgramWasBuilt}: those swaps change
   * `exercise_id` in place, and without this field they read as the user having
   * customized their program (df-4e4a729f2772). The check trusts it only while it still
   * equals `exercise_id`, so a stale value can never hide a real edit.
   *
   * Undefined on every sub-slot written before this field existed, which leaves those
   * answering exactly as they did.
   */
  exerciseIdChosenByApp?: string;
  /**
   * When a coach last saved a change to this exercise (#6428), the same timestamp as that save's
   * `ExerciseChange` journal document. A workout already past this exercise's first set compares
   * it with the copy in its `planSubslotBackup`: when they differ, the coach changed the exercise
   * mid-session and the session writes nothing back into this sub-slot. Read it through
   * {@link coachChangeStampOf}. See `design-docs/coach-exercise-editing.md`.
   */
  coachChange_utc?: string;
  /**
   * The exercise the APP swapped OUT of this sub-slot after a plateau or a weakness result
   * (#6428), so the coach's page can show it as set aside and offer to bring it back. Written by
   * the workout page right after `rotateExerciseInSubSlotByApp`, cleared by
   * `replaceExerciseInSubSlot`. Unknown for swaps made before the field existed. Read it through
   * {@link setAsideExerciseIdOf}.
   */
  setAsideExerciseId?: string;
}

/** the sub-slot's path for one intensity day, or undefined; tolerates a missing or damaged path list */
export function pathAtLevel(subSlot: Plan_SubSlot | undefined, level: IntensityLevel | undefined): Plan_ExercisePath | undefined {
  return subSlot?.exercisePaths?.find(path => path?.intensityLevel === level);
}

/** #6428 R4: was this path's next weight set by hand in the exercise editor, so the next session skips the prescription cap? A damaged value reads as no. */
export function isCoachSetWeight(path: Plan_ExercisePath | undefined): boolean {
  return path?.coachSetWeight === true;
}

/** the sub-slot's coach stamp; a missing or damaged value reads as no stamp */
export function coachChangeStampOf(subSlot: Plan_SubSlot | undefined): string | undefined {
  const stamp = subSlot?.coachChange_utc;
  return typeof stamp === 'string' && stamp !== '' ? stamp : undefined;
}

/**
 * #6428 R9: did someone change this exercise through the editor after the session took its own
 * snapshot (`planSubslotBackup`, taken at the first concluded set)? True when the program's entry
 * carries a different coach stamp than the snapshot. While it is true, the session must not write
 * that sub-slot back into the program: the person's change wins, and today's results on the
 * exercise are judged against it from the next session. No snapshot yet means the session has not
 * concluded a set, and the page still trains on the program's own entry, so the answer is false.
 */
export function coachChangedSinceSnapshot(live: Plan_SubSlot | undefined, snapshot: Plan_SubSlot | undefined): boolean {
  if (!live || !snapshot) {
    return false;
  }
  return coachChangeStampOf(live) !== coachChangeStampOf(snapshot);
}

/** the exercise the app set aside from this sub-slot; a missing or damaged value reads as none */
export function setAsideExerciseIdOf(subSlot: Plan_SubSlot | undefined): string | undefined {
  const id = subSlot?.setAsideExerciseId;
  return typeof id === 'string' && id !== '' ? id : undefined;
}

/**
 * Decide what a subslot keeps when the whole program is rebuilt.
 *
 * A rebuild solves a fresh program and then walks it slot by slot. For each slot it has
 * the slot as the solver just produced it (`newSubSlot`) and, when there was one in the
 * same position before, the slot the user was actually training (`oldSubSlot`). This
 * function is the single rule for what carries over.
 *
 * The rule turns on ONE question: is this still the same exercise? A rebuild is only
 * meant to change which exercises sit in the week and how many sets each one gets, so an
 * exercise that stayed put must keep everything the user has worked up to on it. An
 * exercise that was replaced must start from nothing, because carrying one exercise's
 * weights onto a different one would be worse than making the user re-establish them.
 * A caller with nothing it is willing to carry over says so by passing no old slot.
 *
 * `exercisePaths` is where nearly all of that working state lives: the target weight, the
 * rep target, the minimum reps, the last session's stats, the per-exercise set-count
 * offset, and which kind of set comes next. Emptying it for a slot that did not change is
 * exactly the bug behind #4623 / #4635 / #4986 — the next workout then asks the user to
 * strength-test a lift the app already knows.
 *
 * What does NOT live on the path, and so is not this function's business: the user's
 * typed rep-target preference, which moved to `ExerciseStat.preferRepTargetByIntensity`
 * and is stored per exercise rather than per plan.
 *
 * @param newSubSlot the freshly solved slot, modified in place.
 * @param oldSubSlot the slot that occupied this position before the rebuild. Pass
 *   `undefined` when there was none, and also when nothing may be carried over at all —
 *   the progression-testing "fake plan" path passes `undefined` so its slots always start
 *   clean however well the exercise matches.
 * @param freshSlotIntensity the intensity a slot with nothing to carry over starts at.
 */
export function carrySubSlotStateAcrossRebuild(
  newSubSlot: Plan_SubSlot,
  oldSubSlot: Plan_SubSlot | undefined,
  freshSlotIntensity: IntensityLevel,
): void {
  if (oldSubSlot && oldSubSlot.exercise_id === newSubSlot.exercise_id) {
    // same exercise: everything the user has worked up to on it still applies. That
    // includes volumeAdjustment, the per-exercise set-count offset autoregulation has
    // agreed with them — one step further than this code went before v1.18.0, which
    // carried the paths but reset that field on every rebuild. the one exception is an
    // adjustment that would leave the solver's new base with no sets at all (#6665).
    newSubSlot.exercisePaths = oldSubSlot.exercisePaths ?? [];
    dropAdjustmentsThatEmptyTheExercise(newSubSlot);
    newSubSlot.nextWorkoutIntensity = oldSubSlot.nextWorkoutIntensity;
    if (oldSubSlot.rebuildPathsForIntensity) {
      newSubSlot.rebuildPathsForIntensity = oldSubSlot.rebuildPathsForIntensity;
    }
    newSubSlot.lastTimeConcluded_utc = oldSubSlot.lastTimeConcluded_utc;
    if (oldSubSlot.plateauWorkoutLog_id) {
      newSubSlot.plateauWorkoutLog_id = oldSubSlot.plateauWorkoutLog_id;
    }
    // #6428: a coach's stamp and the app's set-aside record belong to this exercise too
    if (coachChangeStampOf(oldSubSlot)) {
      newSubSlot.coachChange_utc = oldSubSlot.coachChange_utc;
    }
    if (setAsideExerciseIdOf(oldSubSlot)) {
      newSubSlot.setAsideExerciseId = oldSubSlot.setAsideExerciseId;
    }
    return;
  }

  // this slot is new, or its exercise was replaced: nothing from before applies to it
  newSubSlot.exercisePaths = [];
  delete newSubSlot.nextWorkoutIntensity;
  delete newSubSlot.lastTimeConcluded_utc;
  delete newSubSlot.plateauWorkoutLog_id;
  newSubSlot.rebuildPathsForIntensity = freshSlotIntensity;
}

/**
 * Put the training state the user has earned back onto a slot that was just restored from
 * a program backup.
 *
 * This is the third rule in this file about what counts as training state, and the three
 * belong together. {@link carrySubSlotStateAcrossRebuild} answers it for a program
 * rebuild, {@link SUBSLOT_FIELDS_WRITTEN_BY_TRAINING} answers it for "did the user
 * hand-edit anything", and this answers it for "Restore Original Plan" on the Edit
 * Workouts screen.
 *
 * Why the restore needs a rule of its own. `FitnessPlan.generatedPlanBackup` is cloned at
 * build time AFTER the rebuild has already carried the previous program's training state
 * onto the fresh slots, so the backup holds each slot's target weights, rep targets,
 * minimum reps, last-session stats and set-count offset as they stood at BUILD time.
 * Copying the backup straight back therefore rewinds everything the user has worked up to
 * since (#5113). The caller matches each restored slot against the slot the user was
 * actually training and, when it is the same exercise, calls this to put the live training
 * state back on top of the restored one.
 *
 * `trainingLevel` and `intensities` travel WITH the paths and are not optional extras: a
 * path's intensity is looked up as `intensities[trainingLevel][…]`, and accepting a
 * level-up raises `trainingLevel` on the live slot in place. Carrying the paths onto a
 * rewound level would read the wrong intensity row. `seedProgressionFromBackup` in
 * client/src/services/exercise-layoff-restore.ts copies this same seven-field set for the
 * same reason.
 *
 * Every field is assigned OR deleted, so the restored slot ends up mirroring the live one
 * rather than keeping a leftover from the backup. `rebuildPathsForIntensity` is the field
 * that makes this matter: a backup clone can carry a build-time `Initial` flag, and
 * `workout-loader.service.ts` acts on that flag by tearing the paths down and scheduling a
 * strength test — which would throw away the very progression this function just restored.
 *
 * What it deliberately does NOT copy is `defaultVolume_sets`, the base set count. That is
 * the user's own edit from the Exercise Configuration screen, and undoing it is exactly
 * what "Restore Original Plan" promises. Nor `slotId`, `startIndex` or `exercise_id` —
 * the caller matched the two slots on those, so they are already equal.
 *
 * @param restoredSubSlot the slot cloned out of the backup, modified in place.
 * @param liveSubSlot the slot the user was training before the restore.
 */
export function copySubSlotTrainingStateForRestore(restoredSubSlot: Plan_SubSlot, liveSubSlot: Plan_SubSlot): void {
  // deep-clone the two object-valued fields. The live slot the caller hands in is the copy
  // parked in `FitnessState.oldSubslots`, which is a DIFFERENT database document from the
  // plan being restored — assigning by reference would leave one array reachable from both,
  // and a later write through either one would silently change the other. `deepOverwrite`,
  // which the whole-slot restore uses instead of this function, clones for the same reason,
  // and so does `seedProgressionFromBackup` in client/src/services/exercise-layoff-restore.ts.
  restoredSubSlot.exercisePaths = deepClone(liveSubSlot.exercisePaths ?? []);
  restoredSubSlot.trainingLevel = liveSubSlot.trainingLevel;
  restoredSubSlot.intensities = deepClone(liveSubSlot.intensities);
  // the paths now sit on the backup's base, not the live one: an adjustment that would leave
  // that base with no sets is reset (#6665). The paths were cloned above, so the live slot is untouched.
  dropAdjustmentsThatEmptyTheExercise(restoredSubSlot);

  // assign-or-delete for every optional field, so nothing from the backup survives
  if (liveSubSlot.nextWorkoutIntensity) {
    restoredSubSlot.nextWorkoutIntensity = liveSubSlot.nextWorkoutIntensity;
  } else {
    delete restoredSubSlot.nextWorkoutIntensity;
  }
  if (liveSubSlot.rebuildPathsForIntensity) {
    restoredSubSlot.rebuildPathsForIntensity = liveSubSlot.rebuildPathsForIntensity;
  } else {
    delete restoredSubSlot.rebuildPathsForIntensity;
  }
  if (liveSubSlot.lastTimeConcluded_utc) {
    restoredSubSlot.lastTimeConcluded_utc = liveSubSlot.lastTimeConcluded_utc;
  } else {
    delete restoredSubSlot.lastTimeConcluded_utc;
  }
  if (liveSubSlot.plateauWorkoutLog_id) {
    restoredSubSlot.plateauWorkoutLog_id = liveSubSlot.plateauWorkoutLog_id;
  } else {
    delete restoredSubSlot.plateauWorkoutLog_id;
  }
  // #6428
  if (coachChangeStampOf(liveSubSlot)) {
    restoredSubSlot.coachChange_utc = liveSubSlot.coachChange_utc;
  } else {
    delete restoredSubSlot.coachChange_utc;
  }
  if (setAsideExerciseIdOf(liveSubSlot)) {
    restoredSubSlot.setAsideExerciseId = liveSubSlot.setAsideExerciseId;
  } else {
    delete restoredSubSlot.setAsideExerciseId;
  }
}

/**
 * The sub-slot fields that ordinary training writes by itself, as a `deepCompareEQ`
 * ignore map.
 *
 * Every one of these changes without the user editing anything: concluding a set
 * rewrites the paths (target weight, rep target, last session's stats) and stamps
 * `lastTimeConcluded_utc`, moving through a periodization cycle sets
 * `nextWorkoutIntensity`, plateau detection sets `plateauWorkoutLog_id` and
 * `rebuildPathsForIntensity`, and accepting an offered level-up raises
 * `trainingLevel`.
 *
 * This list is deliberately next to {@link carrySubSlotStateAcrossRebuild}, which
 * makes the same split from the other side: the first five entries are exactly what
 * that function carries from the old slot to the new one when the exercise did not
 * change. {@link copySubSlotTrainingStateForRestore} above makes it a third time, for a
 * restore from the program backup. A field added to `Plan_SubSlot` needs all three places
 * thought about together.
 * (`trainingLevel` is not carried over — a rebuild takes the solver's fresh value —
 * but it is still written by training rather than by the user, which is the question
 * this list answers.)
 */
const SUBSLOT_FIELDS_WRITTEN_BY_TRAINING = {
  exercisePaths: true,
  nextWorkoutIntensity: true,
  rebuildPathsForIntensity: true,
  lastTimeConcluded_utc: true,
  plateauWorkoutLog_id: true,
  trainingLevel: true,
  // `deepCompareEQ` takes its ignore map as `any` and matches by key NAME, so a
  // misspelt or renamed entry here would silently exclude nothing at all. This
  // constraint turns that into a compile error, which matters because the comment
  // above asks future editors to add to this list.
} satisfies { [K in keyof Plan_SubSlot]?: true };

/**
 * What {@link hasHandEditsSinceProgramWasBuilt} skips: everything training writes, plus
 * the app's own record of an exercise it chose. That record is not an edit either, and it
 * is never in the backup, so comparing it would make every app rotation read as one.
 * Kept apart from the training list because the comment on that list ties it to the
 * rebuild and restore rules, which this field has no part in.
 */
const SUBSLOT_FIELDS_SKIPPED_BY_HAND_EDIT_CHECK = {
  ...SUBSLOT_FIELDS_WRITTEN_BY_TRAINING,
  exerciseIdChosenByApp: true,
  // written by the app when it swaps an exercise out (#6428), never by a hand edit.
  // `coachChange_utc` is deliberately NOT here: a coach's edit is a hand edit
  setAsideExerciseId: true,
} satisfies { [K in keyof Plan_SubSlot]?: true };

/**
 * The live sub-slots, as the hand-edit check should see them: a sub-slot whose exercise
 * the app itself chose is given back the exercise the backup holds for it, so the swap
 * alone does not count. Only `exercise_id` is put back, and only while the app's record
 * still names the exercise in the slot — a later hand swap clears that record, and a
 * sub-slot with no counterpart in the backup is left alone, so neither can be hidden.
 * Returns a new map and leaves the plan untouched.
 */
function subSlotsWithAppChosenExercisesUndone(
  live: FitnessPlan['subSlots'],
  backup: FitnessPlan['subSlots'],
): FitnessPlan['subSlots'] {
  // if the plan has no sub-slots at all, hand that back so the comparison sees it as it is
  if (!live) {
    return live;
  }
  const result: FitnessPlan['subSlots'] = {};
  for (const id of Object.keys(live)) {
    const liveSubSlot = live[id];
    const backupSubSlot = backup?.[id];
    // if the app put this exercise here and the backup has the slot, compare the backup's exercise
    if (liveSubSlot?.exerciseIdChosenByApp
      && liveSubSlot.exerciseIdChosenByApp === liveSubSlot.exercise_id
      && backupSubSlot) {
      result[id] = { ...liveSubSlot, exercise_id: backupSubSlot.exercise_id };
    } else {
      result[id] = liveSubSlot;
    }
  }
  return result;
}

/**
 * Has the user hand-edited their training program since it was last built?
 *
 * "Hand-edited" means the things a person can change on the Edit Workouts and
 * Exercise Configuration screens: which exercises sit in which session, how many
 * sets each one gets, and the names, order and combo-set grouping of the sessions.
 * Simply having trained is NOT a hand edit, and that is the whole point of this
 * function — see below.
 *
 * The answer is used to pick between two warnings before the program is rebuilt, one
 * of which tells the user they are about to lose their customizations. Asking the
 * question badly is therefore expensive in both directions: too eager and everybody
 * is frightened by a warning that does not apply to them, too lax and somebody loses
 * edits they were never told about.
 *
 * It works by comparing the live program against `generatedPlanBackup`, the copy
 * taken when the program was last built. The comparison skips
 * {@link SUBSLOT_FIELDS_WRITTEN_BY_TRAINING}, because those fields diverge from the
 * backup the moment the user finishes their first set — so before this exclusion
 * existed, every user who had trained at all was told they had customizations they
 * had never made (df-58c3ce82cd3d).
 *
 * Nothing a person can actually edit is skipped. Adding or removing an exercise
 * changes the `subSlots` key set and the workout's `subSlotIds`; swapping one changes
 * `exercise_id`, on a freshly minted sub-slot or on the existing one depending on the
 * route in; a set count is `defaultVolume_sets`; renames, reordering and combo
 * grouping all live on `workouts`. The one edit that does land on a skipped field is
 * a typed rep target, which is stored durably on
 * `ExerciseStat.preferRepTargetByIntensity` outside the plan and is re-applied after
 * a rebuild — so there is nothing there to warn about.
 *
 * The reverse does NOT hold, and the difference matters: a compared field changing is
 * not proof that a person changed it. The app itself rewrites `exercise_id` in place when
 * it swaps out a plateaued or poorly-fitting lift, or rotates away from an avoided,
 * quarantined or deleted custom exercise. Those swaps record the exercise they chose in
 * `exerciseIdChosenByApp`, and this function does not count one as an edit while that
 * record still names the exercise in the slot (df-4e4a729f2772). A hand swap on the same
 * sub-slot clears the record, so it still counts. Sub-slots rotated before that field
 * existed carry no record and still count, until the next rebuild.
 *
 * The load-time plan repairs also rewrite compared fields on old documents — `slotId`,
 * `intensities`, the `subSlots` key set and the days' `subSlotIds`. They apply the same
 * repairs to the backup (`normalizeProgramBackupAtLoad` in the client, df-dadb3d927571), so
 * an old account whose program was repaired does not read as customised.
 *
 * Returns false when there is no backup to compare against: nothing is known, and a
 * warning nobody can justify is worse than no warning.
 */
export function hasHandEditsSinceProgramWasBuilt(plan: FitnessPlan | undefined): boolean {
  if (!plan?.generatedPlanBackup) {
    return false;
  }

  // workouts hold nothing but structure — name, subSlotIds, comboSetGroups — so every
  // difference there is a user edit and none of it needs excluding
  const workoutsMatch = deepCompareEQ(plan.workouts, plan.generatedPlanBackup.workouts);
  const subSlotsMatch = deepCompareEQ(
    subSlotsWithAppChosenExercisesUndone(plan.subSlots, plan.generatedPlanBackup.subSlots),
    plan.generatedPlanBackup.subSlots,
    SUBSLOT_FIELDS_SKIPPED_BY_HAND_EDIT_CHECK,
  );

  return !workoutsMatch || !subSlotsMatch;
}

/**
 * Remove the generated-program backup from a plan clone that is about to be written to
 * the journal as a historical snapshot.
 *
 * `generatedPlanBackup` is a deep copy of `workouts` + `subSlots` as the solver first
 * produced them. It exists so the live plan can be put back to it. Nothing anywhere
 * reads it out of a snapshot — reverting hand edits, the rebuild warning, the login-time
 * migration, the coach-bleed cleanup tool and the last-resort plan recovery all read it
 * from the live `currentplan` document instead. So copying it into every snapshot stores
 * the same program over and over for no reader: on the one account measured for
 * `df-cfa9c57d3c50`, roughly 6.3 MB of 18.7 MB of snapshots was this one field.
 *
 * 🛑 Call this ONLY on a clone that is going into the journal. NEVER call it on a
 * document that can later be installed back as somebody's live plan.
 * `deepOverwriteCouchSafe` deletes every destination key the source does not carry, so
 * installing a plan that has been stripped by this function would erase the live backup
 * and take away the user's ability to revert their edits. The plan copies that ARE
 * installed that way, and so must never be stripped, are `intakeFormState`'s own
 * `generatedFitnessPlan` (which on two paths is the live plan object itself, not a copy),
 * the coach slot's `fitnessPlan`, and `Log_WeeklyCheckup.pendingNewPlan`.
 */
export function stripGeneratedPlanBackupForJournal(plan: FitnessPlan | undefined): void {
  if (plan) {
    delete plan.generatedPlanBackup;
  }
}

/**
 * Whether one training day of the program lists no exercises (#6268).
 *
 * Edit Workouts used to save such a day: an added day left empty, or a day whose every
 * exercise was removed while another day still had some. The training cycle schedules
 * it like any other day, so the user got a blank workout. Edit Workouts now refuses to
 * save one, and the Training page explains one saved before that.
 */
export function workoutHasNoExercises(workout: Plan_Workout | undefined): boolean {
  return !workout?.subSlotIds?.length;
}

/** The program's training days that list no exercises, in program order (#6268). */
export function findEmptyWorkouts(plan: FitnessPlan | undefined): Plan_Workout[] {
  return (plan?.workouts ?? []).filter(workoutHasNoExercises);
}

/**
 * Whether every empty day sits at the END of the program, so deleting them renames no
 * other day (#6268). Past workouts store a day's position (`planReference`), and deleting
 * a day in the middle renames the days after it, which would relabel those workouts — the
 * #5640 problem. A day added with the "+" tab is always last.
 */
export function emptyWorkoutsAreAllAtTheEnd(plan: FitnessPlan | undefined): boolean {
  const workouts = plan?.workouts ?? [];
  const firstEmpty = workouts.findIndex(workoutHasNoExercises);
  // if there is no empty day, there is nothing to delete and nothing to rename
  if (firstEmpty < 0) return true;
  return workouts.slice(firstEmpty).every(workoutHasNoExercises);
}

/**
 * Has this plan been built, yet holds no exercises at all? (#4587)
 *
 * `ready` means the plan was generated at least once, so the app stops offering the
 * set-up questions and starts putting its workouts on the calendar. A plan in that
 * state with no exercises anywhere is not a plan the user can train from: every
 * workout it produces is blank, and nothing in the app notices.
 *
 * A customer reached exactly this state. Her plan lost its list of training days, the
 * load-time heal then deleted every exercise as unreferenced, the emptied plan was
 * saved, and she was left with blank workouts for ever and no way out but deleting all
 * her data. What emptied the day list in the first place was never established; this
 * predicate names the RESULT, so every route to it is caught.
 *
 * Deliberately answers "no" for a plan that was never built: during intake a plan is
 * assembled from nothing, and an empty day list is the normal starting point there. It
 * also answers "no" for somebody who trains no days a week — see `workoutsPerWeek`.
 */
export function planIsBuiltButHasNoExercises(
  plan: FitnessPlan | undefined,
  workoutsPerWeek?: number,
): boolean {
  if (!plan?.ready) {
    return false;
  }
  // Somebody who trains no days a week has no use for exercises, so an empty plan is what
  // they asked for rather than damage (owner's rule). `getWorkoutsPerWeek` in
  // `user-state.ts` reads that off the declared state. The argument is optional and an
  // absent one means "assume they train": a caller that cannot see the declared state must
  // not silently decide the plan is fine, because that is the answer that loses the data.
  if (workoutsPerWeek === 0) {
    return false;
  }
  // a day that references no exercise contributes nothing, so a plan made only of those
  // is as empty as one with no days at all
  for (const workout of plan.workouts ?? []) {
    if (workout?.subSlotIds?.length) {
      return false;
    }
  }
  return true;
}

/** Returns _percentage_! */
export function getSlotPathIntensity(planSlot: Plan_SubSlot, planPath: Plan_ExercisePath) {
  if (
    planSlot.intensities &&
    planSlot.intensities[planSlot.trainingLevel] &&
    planSlot.intensities[planSlot.trainingLevel][planPath.intensityLevel]
  ) {
    let intensity = planSlot.intensities[planSlot.trainingLevel][planPath.intensityLevel];
    return intensity;
  }

  // If we advanced to some higher level but did not yet retest the exercise for that new level, try to find the intensity from the previous level
  let previousTraining: TrainingLevel = undefined;
  switch (planSlot.trainingLevel) {
    case TrainingLevel.Novice:
      break;
    case TrainingLevel.Intermediate:
      previousTraining = TrainingLevel.Novice;
      break;
    case TrainingLevel.Advanced:
      previousTraining = TrainingLevel.Intermediate;
      break;
    default:
  }
  if (
    previousTraining &&
    planSlot.intensities &&
    planSlot.intensities[previousTraining] &&
    planSlot.intensities[previousTraining][planPath.intensityLevel]
  ) {
    let intensity = planSlot.intensities[previousTraining][planPath.intensityLevel];
    return intensity;
  }
  console.error(
    `Undefined intensity for ${planSlot.slotId}$${planSlot.startIndex}@${planSlot.trainingLevel}:${planPath.intensityLevel}!`,
  );
  return 65;
}

/**
 * Decide the set type when a path is about to be promoted to a working ("established") set.
 *
 * A working `Regular1st` set must have a usable target weight. If the weight is missing
 * (undefined / null / NaN), promoting the path to `Regular1st` would create the invalid
 * "target reps but no target weight" state tracked by Sentry 2YK / mh-physique #3752. In
 * that case we keep the path in a test state so the next workout re-establishes the weight
 * cleanly: `RMTest` when the exercise uses RM testing, otherwise `NonRMTest`. This mirrors
 * the heal in `WorkoutLoaderService.fixupInvalidPathState`, but applied at the source (set
 * conclusion) before the bad path is ever persisted and synced.
 *
 * Returns `SetType.Regular1st` only when `target_weight_kg` is a finite number.
 */
export function resolveEstablishedSetType(path: Plan_ExercisePath, usesRMTesting: boolean): SetType {
  if (Number.isFinite(path.target_weight_kg)) {
    return SetType.Regular1st;
  }
  return usesRMTesting ? SetType.RMTest : SetType.NonRMTest;
}

/** Per-intensity rep floors. Unmapped levels (Initial, Intensified) have no entry; each
 *  reader picks its own fallback for them. */
export const MIN_TARGET_REPS_BY_INTENSITY: Readonly<Partial<Record<IntensityLevel, number>>> = {
  [IntensityLevel.DUPlight]: 8,
  [IntensityLevel.DUPheavy]: 4,
  [IntensityLevel.TUPlight]: 9,
  [IntensityLevel.TUPmedium]: 6,
  [IntensityLevel.TUPheavy]: 3,
};

/** Minimum allowed target_reps per intensity path. Used by `setTargetReps` to clamp
 *  rep-progression and by the layoff handler to floor its rep-nudge fallback. */
export function getMinTargetRepsForIntensity(level: IntensityLevel): number {
  return MIN_TARGET_REPS_BY_INTENSITY[level] ?? 4;
}

/** How many reps apart the heavy and light rep targets of a DUP (two intensities a
 *  week) exercise must be. */
export const DUP_REP_TARGET_GAP = 4;
/** How many reps apart adjacent rep targets of a TUP (three intensities a week)
 *  exercise must be: heavy to medium, and medium to light. */
export const TUP_REP_TARGET_GAP = 3;

/** The bounds a rep target at `level` must satisfy, given its siblings' rep targets.
 *  The single source of the DUP/TUP sibling-gap rules:
 *    DUPlight  ≥ DUPheavy  + DUP gap        DUPheavy  ≤ DUPlight - DUP gap
 *    TUPlight  ≥ TUPmedium + TUP gap        TUPheavy  ≤ TUPmedium - TUP gap
 *    TUPmedium between TUPheavy + TUP gap and TUPlight - TUP gap
 *  `min`/`max` are absent when the sibling that sets them has no rep target yet, and
 *  both are absent for a level with no siblings. `gap` is the spacing that applies to
 *  the level (undefined outside DUP/TUP), so a message can state the rule it enforces. */
export function getSiblingRepTargetBounds(
  level: IntensityLevel,
  getSiblingReps: (siblingLevel: IntensityLevel) => number | undefined,
): { min?: number; max?: number; gap?: number } {
  switch (level) {
    case IntensityLevel.DUPlight: {
      const heavy = getSiblingReps(IntensityLevel.DUPheavy);
      return { gap: DUP_REP_TARGET_GAP, ...(heavy !== undefined ? { min: heavy + DUP_REP_TARGET_GAP } : {}) };
    }
    case IntensityLevel.DUPheavy: {
      const light = getSiblingReps(IntensityLevel.DUPlight);
      return { gap: DUP_REP_TARGET_GAP, ...(light !== undefined ? { max: light - DUP_REP_TARGET_GAP } : {}) };
    }
    case IntensityLevel.TUPlight: {
      const medium = getSiblingReps(IntensityLevel.TUPmedium);
      return { gap: TUP_REP_TARGET_GAP, ...(medium !== undefined ? { min: medium + TUP_REP_TARGET_GAP } : {}) };
    }
    case IntensityLevel.TUPheavy: {
      const medium = getSiblingReps(IntensityLevel.TUPmedium);
      return { gap: TUP_REP_TARGET_GAP, ...(medium !== undefined ? { max: medium - TUP_REP_TARGET_GAP } : {}) };
    }
    case IntensityLevel.TUPmedium: {
      const heavy = getSiblingReps(IntensityLevel.TUPheavy);
      const light = getSiblingReps(IntensityLevel.TUPlight);
      return {
        gap: TUP_REP_TARGET_GAP,
        ...(heavy !== undefined ? { min: heavy + TUP_REP_TARGET_GAP } : {}),
        ...(light !== undefined ? { max: light - TUP_REP_TARGET_GAP } : {}),
      };
    }
    default:
      return {};
  }
}

export function getRepTargetForIntensity(intensity_pc: number) {
  if (intensity_pc <= 30) {
    return 28;
  } else if (intensity_pc <= 35) {
    return 26;
  } else if (intensity_pc <= 40) {
    return 24;
  } else if (intensity_pc <= 45) {
    return 22;
  } else if (intensity_pc <= 50) {
    return 20;
  } else if (intensity_pc <= 55) {
    return 18;
  } else if (intensity_pc <= 60) {
    return 16;
  } else if (intensity_pc <= 65) {
    return 14;
  } else if (intensity_pc <= 70) {
    return 12;
  } else if (intensity_pc <= 75) {
    return 10;
  } else if (intensity_pc <= 80) {
    return 8;
  } else if (intensity_pc <= 85) {
    return 6;
  } else if (intensity_pc <= 90) {
    return 4;
  } else /* if (intensity_pc <= 95)*/ {
    return 3;
  }
}

export function getMyoRepsForIntensity(intensity_pc: number) {
  if (intensity_pc <= 60) {
    return 5;
  } else if (intensity_pc <= 65) {
    return 4;
  } else if (intensity_pc <= 70) {
    return 3;
  } else if (intensity_pc <= 75) {
    return 3;
  } else if (intensity_pc <= 80) {
    return 2;
  } else if (intensity_pc <= 85) {
    return 2;
  } else /* if (intensity_pc >=  90 )*/ {
    return 1;
  }
}

export function getMyoRepsForTargetRepsCount(repsCount: number) {
  if (!repsCount || repsCount < 4) {
    return 0;
  } else if (repsCount >= 4 && repsCount <= 5) {
    return 1;
  } else if (repsCount >= 6 && repsCount <= 10) {
    return 2;
  } else if (repsCount >= 11 && repsCount <= 15) {
    return 3;
  } else if (repsCount >= 16 && repsCount <= 19) {
    return 4;
  } else if (repsCount >= 20 && repsCount <= 25) {
    return 5;
  } else /* if (repsCount >= 26)*/ {
    return 6;
  }
}

/** Given a number of classic sets, return equivalent myorep sets in form of number of mini-sets per set.
A set with 0 mini-sets is a classic set.
*/
export function getMyoSetsFromSets(orgSets: number): number[] {
  return [
    /*  0 */[0],
    /*  1 */[0],
    /*  2 */[2],
    /*  3 */[3],
    /*  4 */[4],
    /*  5 */[5],
    /*  6 */[3, 3],
    /*  7 */[4, 3],
    /*  8 */[4, 4],
    /*  9 */[5, 4],
    /* 10 */[5, 5],
    /* 11 */[3, 3, 3],
    /* 12 */[4, 3, 3],
    /* 13 */[4, 4, 3],
    /* 14 */[4, 4, 4],
    /* 15 */[5, 4, 4],
    /* 16 */[5, 5, 4],
    /* 17 */[5, 5, 5],
    /* 18 */[3, 3, 3, 3],
    /* 19 */[4, 3, 3, 3],
    /* 20 */[4, 4, 3, 3],
  ][orgSets] || [0];
}

export class Plan_Workout {
  // "name" (A/B/C...)
  name: string;
  subSlotIds: string[];
  // Maps subSlotId → comboSetNumber (1, 2, 3...). Exercises not in map are individual.
  comboSetGroups?: { [subSlotId: string]: number };
}

export interface FitnessPlanReference {
  timestamp_utc: string;
  BW_kg: number;
  /** BF is unavailable at the beginning for photos-based method, and for later updates it is just an estimate based on original values and progress so far. */
  BF_pc?: number;
  lastAdjustmentDirection?: number;
}

export function updatePlanWithNewBF(plan: FitnessPlan, bf_pc: number, weight_kg: number, timestamp_utc: string) {
  plan.nutritionAdjusted.BF_pc = plan.startPoint.BF_pc = bf_pc;
  plan.nutritionAdjusted.BW_kg = plan.startPoint.BW_kg = weight_kg;
  plan.nutritionAdjusted.timestamp_utc = plan.startPoint.timestamp_utc = timestamp_utc;
}

export enum WelcomePhase {
  Intro = 'intro',
  Intake = 'intake',
  /**
   * Option B of coach-mode onboarding (issue #3389). The client has
   * claimed a blank coach invite and is filling the intake wizard
   * themselves; the wizard runs steps 1-8 (Basic Info through Goals)
   * and stops. Set by coach-claim.service when applyClaimDataAsync
   * detects a ClientFills slot.
   */
  CoachedIntake = 'coached-intake',
  /**
   * Option B of coach-mode onboarding (issue #3389). The client has
   * finished their part of the intake and is held on the "your coach
   * is finalizing your program" screen; the rest of the app is locked
   * until the coach activates the plan. Set by intake-form when the
   * client advances past Goals in coached-onboarding mode.
   */
  AwaitingCoachFinalize = 'awaiting-coach-finalize',
  Done = 'done', /// the user is done with all the welcome phases
}

// This holds the copies of original data received from the server when making the last plan.
// Used to revert eventual user edits to the plan.
export interface TrainingProgramBackup {
  workouts: Plan_Workout[];
  /**
   * The slots exactly as the program build left them — including their training state.
   *
   * This comment used to say the backup holds no progress data. It does: the build clones
   * the plan into the backup AFTER `carrySubSlotStateAcrossRebuild` has put the previous
   * program's paths onto the fresh slots, so every slot here carries the target weights,
   * rep targets and last-session stats it had at BUILD time. Anything restoring from this
   * backup must therefore decide, per slot, what to take from here and what to take from
   * the plan the user has been training since — see
   * {@link copySubSlotTrainingStateForRestore} and `restoreTrainingProgram` in
   * client/src/services/program-backup-restore.ts (#5113).
   */
  subSlots: {
    [subSlotId: string]: Plan_SubSlot;
  };
}
export interface FitnessPlan extends PouchDoc {
  _id: string; // for couch
  ready?: boolean /** set if the plan was generated at least once */;
  welcomePhase?: WelcomePhase;
  tutorialsSeen?: string[];

  /** referential start point in time to calibrate weight loss/gain information against
   * (happens only once when initial intake page is filled out - or when photo-based BF comes in)  */
  startPoint?: FitnessPlanReference;

  /** referential data at the time when the caloric intake was last adjusted
   * (happens once at beginning, and later only if cut/bulk progress is too fast/slow)
   */
  nutritionAdjusted?: FitnessPlanReference;
  shouldInitializeBaselineAtNextReplan?: boolean;
  skipNextNutritionAdjustment?: boolean;

  /** calories per day average across week (because actual are different for cut and bulk meals).
   * This is updated whenever the progress stalls. The `nutritionAdjusted` above is updated whenever this changes. */
  nominalDailyAverageIntake?: number;
  /** this is the value after eventual clamping by the average minimum */
  actualDailyAverageIntake?: number;

  intakeDeltaPercent?: number;
  trainingVolumeWeeklyAvg?: number;

  /** a daily calorie target the user typed, waiting to be put into force. It is a REQUEST,
   * not the target itself — the target is nominalDailyAverageIntake, and only the solver
   * moves the number across. A solve that has a bodyweight and a body-fat reading to work
   * with always consumes the request; a solve that has neither leaves it parked, because it
   * cannot rebuild the meal targets to match, and the next solve applies it (#5181). A stored
   * value of zero or less is never put into force and stays parked. */
  overrideDailyAverageIntake?: number;
  /** true if nominalDailyAverageIntake came from a manual override rather than from the solver.
   * Nothing in the app reads this today — it is kept for inspecting stored plans, so do not
   * assume it drives any behaviour. Cleared whenever the solver recomputes the target. */
  wasOverriddenManually?: boolean;
  /** the nominalDailyAverageIntake before the first ManualOverride was applied.
   * Used to reconstruct "recommended" values when the user re-opens the edit-nutrition-plan page.
   * Cleared when no override is in force any more: when the user resets back to this value, and
   * when initializeBaseline recomputes the target from scratch (#5170). */
  baselineNominalDailyAverageIntake?: number;

  /** macro overrides set by the user via edit-nutrition-plan page.
   * Unlike overrideDailyAverageIntake (one-shot), these persist across plan updates.
   * Cleared when user clicks "Reset to Recommended". */
  overrideMacros?: {
    protein_g_per_day?: number;
    carbs_g_per_day?: number;
    fat_g_per_day?: number;
    fiber_g_per_day?: number;
    /** carb/fat split ratio (0-1), portion of remaining kcal going to carbs. Only for fixed nutrition. */
    carbFatRatio?: number;
    /** UI state: when true, macro inputs auto-recalculate when calories change (fixed nutrition only) */
    autoAdjust?: boolean;
  };

  workouts: Plan_Workout[];
  /** NOTE: Subslots have both slot id and the $<num> suffix for the exercise offset! */
  subSlots: {
    [subSlotId: string]: Plan_SubSlot;
  };

  /** Backup of the original generated plan structure for reverting user edits */
  generatedPlanBackup?: TrainingProgramBackup;

  /** These levels are used to track when to revamp the program. Actual exercise periodization is decided by per-exercise levels, which may be behind these here. */
  trainingLevels: TrainingLevels;
  bodyCompositionType?: BodyCompositionType;
  needsReplan?: boolean;
  lastCheckup_utc?: string;
  resetNutritionQueued?: boolean;
  weeklyTargets?: { [key: string]: number };
  cutMealTargets?: { [key: string]: number };
  bulkMealTargets?: { [key: string]: number };
  fixedMealTargets?: { [key: string]: number };

  /// This is set when some weigh-in is changed. Used to see when to call the cloud function to properly update `needsReadjustment`
  shouldCheckWhetherNeedsReadjustment?: boolean;
  needsReadjustment?: boolean;
  readjustmentReason?: AdjustmentActionReason;
  lastTimeMissedNutritionMacros_utc?: string;
}

/**
 * Back-compat migration for the plan's `welcomePhase` field. Extracted from
 * the client's `backCompatFitnessPlan` (user-db.service.ts), which runs it on
 * every plan load. Returns true when the plan was changed and needs saving.
 */
export function backCompatWelcomePhase(plan: FitnessPlan): boolean {
  let needsSaving = false;

  // #4125: the option-B coach onboarding phases legitimately coexist with a
  // ready plan — a client who already had a generated program claims a
  // "client fills the intake" coach invite, which sets CoachedIntake (and
  // later AwaitingCoachFinalize) while the old plan stays ready. these are
  // deliberate modern states, not legacy leftovers — never heal them to Done,
  // or the coached onboarding silently disappears on the next plan load.
  const isCoachOnboardingPhase = plan.welcomePhase === WelcomePhase.CoachedIntake
    || plan.welcomePhase === WelcomePhase.AwaitingCoachFinalize;

  // handling of old accounts before welcome flow was introduced.
  // `Intake` is the default state that old objects will load into (as they don't have the field)
  // but if they have a ready plan, then it means that they don't need the welcome flow
  if (plan.welcomePhase !== WelcomePhase.Done && plan.ready && !isCoachOnboardingPhase) {
    plan.welcomePhase = WelcomePhase.Done;
    needsSaving = true;
  }

  // migrate old welcome phase enum values that no longer exist
  if (plan.welcomePhase === 'nutrition' as any || plan.welcomePhase === 'schedule' as any) {
    plan.welcomePhase = WelcomePhase.Done;
    needsSaving = true;
  }

  return needsSaving;
}

export function parseSubslotIdAndOffset(subSlotId: string): { parsedSlotId: string; initialExerciseIndex: number } {
  let [, id, offset] = subSlotId.match(/(\$[^$]+)(\$\d+)?/) || ['', '$squat', null];
  let parsedSlotId = id;
  let initialExerciseIndex = offset ? parseInt(offset.slice(1)) : 0;
  return { parsedSlotId, initialExerciseIndex };
}

// remove the 'slot/' prefix from the slot id
export function parseShortSlotIdFromLong(longSlotId: string): string {
  console.assert(longSlotId && longSlotId.startsWith('slot/'), 'Slot ID must start with "slot/"');
  return longSlotId.slice(5);
}

export function constructSubslotIdFromParts(slotId: string, initialExerciseIndex: number) {
  console.assert(slotId && slotId[0] === '$', 'Slot ID must start with $')
  return `${slotId}$${initialExerciseIndex.toFixed(0)}`;
}

export function getSubslotId(subslot: Plan_SubSlot) {
  // strip the "slot/" prefix from the slot id
  return constructSubslotIdFromParts(parseShortSlotIdFromLong(subslot.slotId), subslot.startIndex);
}

/** The id of the subslot that already holds this exercise anywhere in the plan, or undefined
 *  when the exercise is not in the plan at all. Adding or switching to an exercise the plan
 *  already holds REUSES this subslot (`resolveSubslotForNewExerciseAsync` in the client's
 *  exercise-switching.ts), so every workout listing it shares one progression. */
export function findSubslotIdHoldingExercise(plan: FitnessPlan, exerciseId: string): string | undefined {
  return Object.keys(plan.subSlots).find(k => plan.subSlots[k].exercise_id === exerciseId);
}

/** The workouts, other than `targetWorkoutName`, that would share one progression with the
 *  target workout if this exercise were added to it or switched into it: every other workout
 *  listing the subslot {@link findSubslotIdHoldingExercise} would reuse. Empty when the
 *  exercise is not in the plan, or only in the target workout. The exercise picker asks the
 *  user before such a link is made (#4004). See design-docs/exercise-replacement.md. */
export function otherWorkoutsSharingExercise(plan: FitnessPlan, exerciseId: string, targetWorkoutName: string): Plan_Workout[] {
  const subSlotId = findSubslotIdHoldingExercise(plan, exerciseId);
  if (!subSlotId) {
    return [];
  }
  return plan.workouts.filter(w => w.name !== targetWorkoutName && w.subSlotIds?.includes(subSlotId));
}

/** Whether `workout` already lists the subslot {@link findSubslotIdHoldingExercise} would reuse
 *  for this exercise. Changing another exercise of that workout into this one would then list
 *  the subslot twice, so the exercise picker, `performExerciseSwitchAsync` and
 *  `changeExerciseInWorkoutAsync` all refuse it through this one check (#6296).
 *  `exceptSubSlotId` is the subslot being changed: keeping its own exercise lists nothing twice.
 *  See design-docs/exercise-replacement.md. */
export function workoutAlreadyListsExercise(plan: FitnessPlan, exerciseId: string, workout: Plan_Workout | undefined, exceptSubSlotId?: string): boolean {
  const subSlotId = findSubslotIdHoldingExercise(plan, exerciseId);
  if (!subSlotId || subSlotId === exceptSubSlotId) {
    return false;
  }
  return !!workout?.subSlotIds?.includes(subSlotId);
}

export function findOldSubslot(fitnessState: FitnessState, subSlotId: string, exerciseId: string): Plan_SubSlot {
  if (!subSlotId || !exerciseId) {
    return undefined;
  }
  let { parsedSlotId, initialExerciseIndex } = parseSubslotIdAndOffset(subSlotId);
  parsedSlotId = 'slot/' + parsedSlotId;
  return fitnessState.oldSubslots && fitnessState.oldSubslots.find(ss => ss.slotId === parsedSlotId && ss.startIndex === initialExerciseIndex && ss.exercise_id === exerciseId)
}

export type PerMuscleVolume = {
  [muscleId in MuscleId]?: number;
};

// Calculate per-muscle volume for the given plan
// muscleFactorOverrides: optional per-subSlotId overrides for custom exercises with a "similar exercise" reference
// uncountedSubSlotIds: optional out-array. The function appends the subSlotId of every slot that is
//   present in plan.subSlots but could not be attributed to any muscle (no constants-table entry for
//   the slot AND no override) — e.g. a generic $custom slot whose custom exercise has no "Most Similar
//   Exercise" set. These exercises contribute zero to the totals; collecting them lets the caller tell
//   the user some exercises are not counted, instead of showing a silently-wrong total (#3790). This
//   is distinct from a true orphan (a subSlotId in a workout with no plan.subSlots entry — see #3282,
//   reported separately by reportOrphanSubSlotIds).
// usesMyoRepsBySubSlotId: optional map saying which sub-slots are scheduled as myo-reps. Deciding
//   that needs the Exercise document and the user's state, which this function does not take, so
//   the caller works it out (see buildUsesMyoRepsBySubSlotAsync in
//   client/src/util/muscle-volume-helpers.ts). Leaving it out counts every exercise as classic
//   sets, which is what every caller did before #4523.
export function getMuscleVolumeForPlan(plan: FitnessPlan, workoutsPerWeek: number, muscleFactorOverrides?: { [subSlotId: string]: MuscleFactors }, uncountedSubSlotIds?: string[], usesMyoRepsBySubSlotId?: { [subSlotId: string]: boolean }): PerMuscleVolume {
  let slotConstantsTable = getExerciseConstantsTable();

  // volume per week calculated from one workout depends on number of splits and frequency of the workouts
  const numSplits = plan.workouts.length;
  const volumePerWeekFactor = workoutsPerWeek / numSplits;

  // for each exercise in every workout
  const muscleVolume: PerMuscleVolume = {};
  for (const workout of plan.workouts) {
    for (const subSlotId of workout.subSlotIds) {
      let subSlot = plan.subSlots[subSlotId];
      if (!subSlot) {
        // orphan id in workout.subSlotIds without matching entry in plan.subSlots —
        // see #3282; client-side callers detect and report via LoggingService.trackSilentProblem
        console.warn(`No subSlot found for subSlotId ${subSlotId} — skipping in muscle-volume calc`);
        continue;
      }
      const { parsedSlotId, initialExerciseIndex } = parseSubslotIdAndOffset(subSlotId);
      void initialExerciseIndex;

      // if custom exercise has a "similar exercise" override, use its muscle factors
      const overrideFactors = muscleFactorOverrides?.[subSlotId];
      let muscleFactors: MuscleFactors;
      if (overrideFactors) {
        muscleFactors = overrideFactors;
      } else {
        const exerciseConstant: ExerciseConstants = slotConstantsTable[parsedSlotId];
        if (!exerciseConstant) {
          // the slot has no entry in the constants table and no override — typically a generic
          // $custom slot whose custom exercise has no "Most Similar Exercise" set, so we have no
          // muscle data for it. It contributes zero. Record it so the caller can surface that some
          // exercises are not counted, rather than silently presenting a wrong total (#3790).
          console.warn(`No exercise constants found for subSlotId ${subSlotId}`);
          // dedup: the same subSlotId can be referenced by more than one workout, but the
          // uncounted list must hold each exercise once (the UI uses it as a track key, and the
          // count must not be inflated). The per-muscle volume sum below intentionally still
          // double-counts cross-workout references — that path is divided by numSplits and is correct.
          if (uncountedSubSlotIds && !uncountedSubSlotIds.includes(subSlotId)) {
            uncountedSubSlotIds.push(subSlotId);
          }
          continue;
        }
        muscleFactors = exerciseConstant.muscleFactors;
      }

      // get the number of sets for this exercise in this workout. this is the count the
      // user's workouts really hold, so it includes a set they gained by accepting the
      // app's own volume suggestion during training, which used to go uncounted (#4523).
      let numSets = getEffectivePlanSetCount(subSlot, !!usesMyoRepsBySubSlotId?.[subSlotId]);

      // for each muscle in the exercise
      for (const muscleId in muscleFactors) {
        // calculate the weekly volume contribution from this particular exercise
        const muscleFactor = muscleFactors[muscleId];
        const volume = muscleFactor * numSets * volumePerWeekFactor;
        // accumulate the volume for this muscle
        muscleVolume[muscleId] = (muscleVolume[muscleId] || 0) + volume;
      }
    }
  }
  return muscleVolume;
}